import { getYear } from 'date-fns';
import moment from 'moment';
import { groupBy } from 'ramda';
import { BOND_TYPE_ENUM } from 'lib/constants';

const removeSpaceInTenor = (tenor) => {
    if (typeof tenor !== 'string') throw new Error('invalid tenor');
    return String(tenor).replace(/ /, '');
};
const addSpaceToTenor = (tenor) => {
    if (typeof tenor !== 'string') throw new Error('invalid tenor');
    const ip = removeSpaceInTenor(tenor);

    return `${ip.substring(0, ip.length - 1)} ${ip.substring(ip.length - 1)}`;
};

const addTermToDate2 = (term, curveDate = new Date()) => {
    const isLeapYear = (year) => moment(new Date(year, 0, 1)).isLeapYear();

    const addDays = (date, days) => moment(new Date(date)).add(days, 'days').toDate();
    const addMonths = (date, months) => moment(new Date(date)).add(months, 'months').toDate();
    const addYears = (date, years) => moment(new Date(date)).add(years, 'years').toDate();

    const monthDate = (val, refDate) => {
        const months = Math.floor(val);
        const monthDate = addMonths(refDate, months);
        const month = monthDate.getMonth() + 1;

        if (month === 2) {
            const daysInFeb = isLeapYear(monthDate.getFullYear()) ? 29 : 28;
            return addDays(monthDate, Math.round((val - months) * daysInFeb));
        }

        if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
            return addDays(monthDate, Math.round((val - months) * 31));
        }

        return addDays(monthDate, Math.round((val - months) * 30));
    };

    const parts = addSpaceToTenor(term).toUpperCase().split(' ');
    if (parts.length !== 2) {
        throw new Error('invalid date');
    }

    const part1 = parseFloat(parts[0]);
    if (Number.isNaN(part1)) throw new Error('invalid term duration');

    const part2 = parts[1];
    switch (part2) {
        case 'D':
            return addDays(curveDate, Math.floor(part1));
        case 'W':
            return addDays(curveDate, Math.floor(part1 * 7));
        case 'M':
            return monthDate(part1, curveDate);
        case 'Y': {
            const years = Math.floor(part1);
            const yearDate = addYears(curveDate, years);
            const months = (part1 - years) * 12;
            return monthDate(months, yearDate);
        }
        default:
            throw new Error('invalid term symbol');
    }
};

const getTermInMonths = (term) => {
    const [duration, unit] = term.split(' ');
    return unit.toLowerCase() === 'm' ? duration : 12 * duration;
};

export const normalizeTenor = (text) => {
    const regex = /^((\d*)(\.\d+)?)([mywd])$/i;
    const m = text?.match(regex);
    if (!m) return null;

    const intPart = m[2] || '0';
    const fracPart = m[3] || '';
    const unit = m[4];

    const full = intPart + fracPart + unit;

    return [full, intPart + fracPart, intPart, fracPart || '', unit];
};

const getTermInString = (term) => {
    const [duration, unit] = term.split(' ');
    const units = {
        m: 'Month',
        y: 'Year',
        w: 'Week',
        d: 'Day'
    };
    return `${duration} ${units[unit.toLowerCase()]}(s)`;
};

export const getTenor = (t) => {
    const tenorLower = t?.toLowerCase().replace(/ /g, '');
    const matchTenor = normalizeTenor(tenorLower);
    if (matchTenor) {
        const number = matchTenor[1];
        const term = matchTenor[4].toUpperCase();
        return `${number} ${term}`;
    }
    return null;
};

const calculateTenure = (s, e) => {
    const label = e.label.replace(/ /g, '').toLowerCase();
    const matches = label.match(/^(\d+)([dwmy])$/);
    if (matches) {
        let value = parseInt(matches[1], 10);
        const unit = matches[2];
        if (unit === 'm') value /= 12;
        if (unit === 'w') value /= 52;
        if (unit === 'd') value /= 365;
        return value;
    }
    return moment(e.value).diff(s.value, 'days') / 365;
};

const formatPrice = ({ isIrs = false, isFx = false, value }) => {
    if (isFx) return `${value}%`;
    if (!isIrs) return value;
    const fractionDigits = Number(value).toString().split('.')[1];
    return Number(value).toFixed(
        Math.max(2, Math.min(6, fractionDigits?.length) || 0),
    );
};
const formatIrsPrice = ({ value }) => {
    const fractionDigits = Number(value).toString().split('.')[1];
    return Number(value).toFixed(
        Math.max(2, Math.min(6, fractionDigits?.length) || 0),
    );
};

const getSubAssetType = (type = '', data = {}) => {
    switch (type) {
        case 'cds':
            return 'Cds';
        case 'bond':
            return data?.bondLevel;
        case 'ndf':
            return data?.end1 ? 'Liquidity Swaps' : 'Outright';
        case 'fx':
            return 'FxOptions';
        case 'irs':
            if (data.end && !data.end1 && !data.end2) return 'Outright';
            if (data.end && data.end1 && !data.end2) return 'Spread';
            if (data.end && data.end1 && data.end2) return 'Butterfly';
            return '';
        default:
            return '';
    }
};

const assestTypeMapping = {
    CURRENCY: 'NDF',
    IRS: 'IRS',
    BOND: 'BOND',
    CDS: 'CDS'
};

const byYear = groupBy((item) => getYear(new Date(item.value)));

const throttleFuncCb = (liveData) => {
    if (!liveData) return null;
    const rate = liveData
        && Number(liveData?.s ? (liveData.a + liveData.b) / 2 : liveData?.close);
    const change = liveData
        && Number(liveData?.dd ? liveData?.dd : liveData.close - liveData.open);
    const percentChange = liveData
        && Number(liveData?.dc ? liveData?.dc : (change / liveData.close) * 100);

    return {
        rate: Number(rate),
        change: Number(change),
        percentChange: Number(percentChange)
    };
};

const parseTenor = (tenor) => {
    const multipliers = {
        d: 1,
        w: 7,
        m: 30,
        y: 365
    };
    const match = tenor.toLowerCase().match(/^((?:\d+(?:\.\d+)?|\.\d+))\s*([dwmy])$/);
    if (!match) return null;
    const [, num, unit] = match;
    return parseFloat(num) * multipliers[unit];
};

const isMeetingDates = (bond) => bond && bond?.type === 'meetingDates';

const getYearFromDate = (date, start = new Date()) => Number(
    Number(
        moment(date, 'YYYY-MM-DD')
            .startOf('day')
            .diff(moment(start, 'YYYY-MM-DD').startOf('day'), 'days', true) / 365,
    ).toFixed(4),
);

const isMeetingDatePattern = (str) => /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\d{2}\s[A-Za-z]+(\([A-Za-z]+\))?$/.test(str);
const isImmDate = (str) => /^[MUZH]\s*\d{1,2}$/.test(str);

const reorderPairs = (legs) => {
    const parseTenorValue = (t) => parseFloat(t);

    if (legs.length !== 4) return legs;
    const firstPair = legs.slice(0, 2);
    const secondPair = legs.slice(2, 4);

    const firstPairFirst = parseTenorValue(firstPair[0]);
    const secondPairFirst = parseTenorValue(secondPair[0]);

    const firstPairSecond = parseTenorValue(firstPair[1]);
    const secondPairSecond = parseTenorValue(secondPair[1]);

    const firstGroupComesFirst = firstPairFirst > secondPairFirst
        || (firstPairFirst === secondPairFirst && firstPairSecond > secondPairSecond);

    return firstGroupComesFirst ? legs : [...secondPair, ...firstPair];
};

const IMM_DEFAULT_MAX_YEAR = 30;
/** Last calendar year shown for IRS start-date IMM chips (e.g. Z60). */
const IMM_START_MAX_CALENDAR_YEAR = 2060;
const IMM_EXPANDED_MAX_YEAR = IMM_START_MAX_CALENDAR_YEAR % 100;
/** Years ahead to request from /get/imm so the ladder includes IMM_START_MAX_CALENDAR_YEAR. */
const getImmYearsAhead = (from = new Date()) => Math.max(
    IMM_START_MAX_CALENDAR_YEAR - getYear(new Date(from)) + 1,
    1,
);
const getImmYearSuffix = (item) => {
    if (!item?.value) return null;
    const year = getYear(new Date(item.value));
    return Number.isNaN(year) ? null : year % 100;
};

export const shouldShowIrsMarketTabs = ({
    isTabsVisible,
    command,
    tradeId
}) => {
    if (!isTabsVisible) return false;
    if (tradeId && !command) return false;
    return true;
};

const variantSuffix = (mdType) => {
    if (mdType === 'fullDate') return ' (Full Dt)';
    if (mdType === 'stub') return ' (Stub)';
    return '';
};

export const isIrsButterflyFromStructure = (structureStr) => {
    if (!structureStr || typeof structureStr !== 'string') return false;
    const [, legsPart] = structureStr.split(' | ');
    if (!legsPart) return false;
    return legsPart.split(' X ').length === 3;
};

export const isIrsButterfly = (data = {}) => {
    if (!data) return false;
    if (data.end2 || data.end2Label) return true;
    if ([BOND_TYPE_ENUM.BOB_BUTTERFLY, BOND_TYPE_ENUM.SIXS_BUTTERFLY].includes(data.type)) return true;
    if (data.s || data.structure) return isIrsButterflyFromStructure(data.s || data.structure);
    return false;
};

// A fly is quoted on its belly (leg2) dv01; each wing carries half of it.
export const getStructureLegDv01 = ({ dv01, isButterfly, identifier }) => (
    isButterfly && identifier !== 'leg2' ? dv01 / 2 : dv01
);

const parseRegressFamilyLegs = (variable) => (
    (String(variable || '').match(/[HMUZ]\d{2}|\d+\s*[YMWD]/gi) || []).map((leg) => leg.replace(/\s+/g, '').toUpperCase())
);

export {
    assestTypeMapping,
    byYear,
    calculateTenure,
    formatPrice,
    getSubAssetType,
    getTermInMonths,
    getTermInString,
    throttleFuncCb,
    parseTenor,
    isMeetingDates,
    getYearFromDate,
    isMeetingDatePattern,
    isImmDate,
    formatIrsPrice,
    reorderPairs,
    addTermToDate2,
    addSpaceToTenor,
    removeSpaceInTenor,
    variantSuffix,
    IMM_DEFAULT_MAX_YEAR,
    IMM_EXPANDED_MAX_YEAR,
    IMM_START_MAX_CALENDAR_YEAR,
    getImmYearsAhead,
    getImmYearSuffix,
    parseRegressFamilyLegs
};

export const tenorToMonths = (tenor) => {
    const match = tenor.match(/(\d+(?:\.\d+)?)([mywd])/i);
    if (!match) return null;
    const [, num, unit] = match;
    const val = parseFloat(num);
    const units = {
        d: val / 30,
        w: val / 4,
        m: val,
        y: val * 12
    };
    return units[unit.toLowerCase()] || null;
};

export const monthsToTenor = (months) => {
    if (months < 1) return '1D';
    if (months < 12) return `${Math.round(months)}M`;
    if (months % 12 === 0) return `${months / 12}Y`;
    return `${Math.round(months)}M`;
};

export const formatStructureFromQuery = ({ legs, isImm, isFwd, validImm }) => {
    const labeledLegs = legs.map((leg) => {
        const immMatch = validImm
            .find((imm) => String(imm.y) === leg.replace(/[ymdw]/, ''))
            ?.label.toLowerCase() || '';
        return immMatch || leg;
    });

    if (isFwd) {
        if (labeledLegs.length === 4 || labeledLegs.length === 6) {
            const chunkSize = 2;
            const chunks = [];

            for (let i = 0; i < labeledLegs.length; i += chunkSize) {
                chunks.push(labeledLegs.slice(i, i + chunkSize).join(''));
            }

            return chunks.join(' ').replace(/(?:^|\s)0y/gi, ' ');
        }
        return labeledLegs.join(!isImm ? '' : ' ').replace(/(?:^|\s)0y/gi, ' ');
    }

    return labeledLegs
        .reverse()
        .join(' ')
        .replace(/(?:^|\s)0y/gi, ' ');
};

export const getCurveColor = (name) => {
    name = name.toLowerCase().trim();
    let hash = 0;

    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xff;

        // avoid too dark colors
        value = Math.floor((value + 128) / 2);

        color += (`00${value.toString(16)}`).slice(-2);
    }

    return color;
};
